/* The small pieces the attendance tabs share: the sheet, the footnote under
 * a panel, the bar-in-a-track, and the page's own palette. */

import type { CSSProperties, ReactNode } from 'react'
import { Blueprint } from '../../components/ui'
import { num } from '../../lib/format'

export const OK = 'var(--color-good)'
export const BAD = 'var(--color-bad)'
export const R: CSSProperties = { textAlign: 'right' }

/** '−12.4' or '+3.0' — a gap, signed the way the design writes it. */
export const signed = (v: number, d = 0) => (v < 0 ? '−' : '+') + num(Math.abs(v), d)

/** Colours worked out in JS rather than CSS: the five shift bands of the
 *  stacked bars and the three coverage bands of the shift × day grid, which
 *  the design defines per theme and which have no token of their own. Weak
 *  coverage is the palest band and strong the deepest, in both themes. */
export function pal(dark: boolean) {
  return {
    shift: dark
      ? ['#4f8cff', '#6fa2ff', '#3b6fd6', '#8fb4ff', '#2a3555']
      : ['#5980a6', '#94bce3', '#416180', '#749dc4', '#c9ced4'],
    covWeak: dark ? '#cdd8ee' : '#eef3f8',
    covWeakFg: dark ? '#333f5c' : '#5b6a78',
    covMid: dark ? '#6f86b5' : '#9fbdd8',
    covMidFg: dark ? '#ffffff' : '#12212e',
    covStrong: dark ? '#2b3a63' : '#1d2d3d',
    covStrongFg: dark ? '#c3d3f2' : '#f2f2f3',
  }
}

export function Sheet({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <Blueprint className="sheet" style={{ padding: '22px 24px 26px', ...style }}>
      {children}
    </Blueprint>
  )
}

/** The line under a panel's title. */
export function Sub({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 4 }}>{children}</div>
  )
}

/** The reading note at the foot of a panel. */
export function Foot({ children, top = 18, wide = true }: {
  children: ReactNode; top?: number; wide?: boolean
}) {
  return (
    <div className="pretty" style={{
      fontSize: 12, color: 'var(--color-neutral-700)', marginTop: top, lineHeight: 1.5,
      maxWidth: wide ? '100ch' : undefined,
    }}>{children}</div>
  )
}

/** A bar in a track, with an optional tick where the requirement sits. */
export function Track({ width, height, value, fill = 'var(--c-bar1)', tick, stretch = true, inset = 0 }: {
  /** viewBox width — `value` and `tick` are in these units. */
  width: number
  height: number
  value: number
  fill?: string
  tick?: number | null
  /** Fill the grid cell; false keeps the bar at its viewBox size. */
  stretch?: boolean
  /** Rows above and below the bar left clear, for the thin table bars. */
  inset?: number
}) {
  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio={stretch ? 'none' : undefined}
      style={{
        width: stretch ? '100%' : width, height, display: 'block',
        flex: stretch ? undefined : 'none',
      }}>
      <rect x="0" y={inset} width={width} height={height - inset * 2} fill="var(--c-track)" />
      <rect x="0" y={inset} width={Math.max(0, value)} height={height - inset * 2} fill={fill} />
      {tick != null ? (
        <line x1={tick} y1="0" x2={tick} y2={height} stroke="var(--c-ink)" strokeWidth="2"
          vectorEffect="non-scaling-stroke" />
      ) : null}
    </svg>
  )
}

/** Label, bar, value — the row the late, punch-miss and overtime panels
 *  are built from. */
export function RateRow({ label, width, fill, value, aside }: {
  label: string; width: number; fill: string; value: string; aside: string
}) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '104px minmax(120px,1fr) 112px',
      gap: 12, alignItems: 'center',
    }}>
      <div className="truncate" style={{ fontSize: 13, color: 'var(--color-neutral-800)' }}>{label}</div>
      <Track width={300} height={14} value={width} fill={fill} />
      <div className="num" style={{ fontSize: 13, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {value}<span style={{ color: 'var(--color-neutral-500)' }}> · {aside}</span>
      </div>
    </div>
  )
}

/** 'RING FRAME SIDER' → 'Ring Frame Sider' — the register's capitals, as
 *  the design writes a designation. */
export const titleCase = (s: string) =>
  s.toLowerCase().replace(/(^|[\s&/-])([a-z])/g, (_m, a: string, b: string) => a + b.toUpperCase())

/** '1 worker', '198 workers'. */
export const plural = (n: number, noun: string) => `${num(n)} ${noun}${n === 1 ? '' : 's'}`

/** Which group the page is showing, as the noun the copy uses. The
 *  server's `who` is the singular; every tab takes this and words itself
 *  from it rather than testing `who` in each sentence. */
export type People = 'workers' | 'staff'

/** The words that change with the group: the group itself, one of them,
 *  and what the requirement sheet's finest lines are for it — the sheet
 *  names designations for workers and only departments for staff. */
export function words(people: People) {
  return people === 'staff'
    ? { many: 'staff', Many: 'Staff', one: 'staff member', line: 'department',
        lines: 'departments', Lines: 'Departments' }
    : { many: 'workers', Many: 'Workers', one: 'worker', line: 'designation',
        lines: 'designations', Lines: 'Designations' }
}

/** '198 workers', '1 worker', '64 staff'. */
export const heads = (n: number, people: People) =>
  (people === 'staff' ? `${num(n)} staff` : plural(n, 'worker'))

/** 'A', 'A and B', 'A, B and C'. */
export function listOf(names: string[]): string {
  if (names.length < 2) return names.join('')
  return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
}
